import './applicantEmploymentDurationPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { HALF_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/halfYear';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    currentIncomeMonthsAt,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setCurrentIncomeMonthsAt,
    syncIncome,
  } = useApplicantStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      monthsAtIncomeValue: currentIncomeMonthsAt,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const monthsAtIncomeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(currentIncomeMonthsAt) && isValidClickEvent(event),
    );

    setCurrentIncomeMonthsAt(monthsAtIncomeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { monthsAtIncomeValue } = values;

    if (!monthsAtIncomeValue) {
      return;
    }

    // Always sync income on form submit in case user did not provide a duration initially
    const synced = await syncIncome();
    if (synced) {
      navigate(next());
    }
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-employment-duration-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.applicantEmploymentDuration.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantEmploymentDuration.header
                .previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantEmploymentDuration.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={
              locator.pages.applicantEmploymentDuration.progress.root
            }
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantEmploymentDuration.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.applicantEmploymentDuration.form.h1}
              >
                {t('ApplicantEmploymentDurationPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.applicantEmploymentDuration.form.h2}
              >
                {t('ApplicantEmploymentDurationPage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantEmploymentDuration.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={currentIncomeMonthsAt}
                  labelPlacement="below"
                  name="monthsAtIncomeValue"
                  options={[
                    {
                      label: t('ApplicantEmploymentDurationPage.yes'),
                      value: ONE_YEAR_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.applicantEmploymentDuration.form
                          .radioOption1,
                    },
                    {
                      label: t('ApplicantEmploymentDurationPage.no'),
                      value: HALF_YEAR_IN_MONTHS_AS_STRING,
                      testId:
                        locator.pages.applicantEmploymentDuration.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantEmploymentDuration.form
                      .radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.applicantEmploymentDuration.form.submitButton
                  }
                  disabled={isEmpty(currentIncomeMonthsAt)}
                  loading={isSyncingIncome}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantEmploymentDurationPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.applicantEmploymentDuration.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEmploymentDurationPage';
